import { useState, useEffect, useCallback } from 'react';
import {
    IconPlus,
    IconSearch,
    IconX,
    IconEdit,
    IconTrash,
    IconSettings,
    IconChevronLeft,
    IconUser,
    IconBriefcase,
    IconUsersGroup,
    IconCopy,
    IconCheck,
    IconEye,
    IconPrinter,
    IconDownload,
    IconDotsVertical,
    IconMessage,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@/lib/notifications';
import { useDebouncedValue } from '@/lib/hooks';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import customerService from '../../../services/sohozJobCustomerService';
import CustomerDataModal from './CustomerDataModal';

export default function SohozJobList() {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebouncedValue(search, 300);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [copiedId, setCopiedId] = useState(null);
    const [docModal, setDocModal] = useState(null); // { customerId, action }

    const copyPhone = async (e, c) => {
        e.stopPropagation();
        if (!c.mobile_number) return;
        try {
            await navigator.clipboard.writeText(c.mobile_number);
            setCopiedId(c.id);
            setTimeout(() => setCopiedId((id) => (id === c.id ? null : id)), 1500);
        } catch { /* clipboard blocked */ }
    };

    const fetchCustomers = useCallback(async () => {
        setLoading(true);
        try {
            const res = await customerService.list({ search: debouncedSearch });
            setCustomers(res.data || []);
        } catch {
            notifications.show({ title: t('common.error'), message: t('sohozJob.failedToLoad'), color: 'red' });
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch]);

    useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

    const handleCreate = () => navigate('/tools/form-auto-fillup/customers/new');
    const handleEdit = (customer) => navigate(`/tools/form-auto-fillup/customers/${customer.id}/edit`);

    const handleDeleteConfirm = async () => {
        try {
            await customerService.remove(deleteTarget.id);
            notifications.show({ title: t('common.success'), message: t('sohozJob.customerDeleted'), color: 'green' });
            setDeleteTarget(null);
            fetchCustomers();
        } catch {
            notifications.show({ title: t('common.error'), message: t('sohozJob.failedToDelete'), color: 'red' });
        }
    };

    return (
        <div className="w-full font-hind space-y-5 sm:space-y-6 py-2 sm:py-4">

            {/* ── Header ── */}
            <div>
                <button
                    onClick={() => navigate('/tools')}
                    className="inline-flex items-center gap-1 text-[13px] font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors mb-3"
                >
                    <IconChevronLeft size={15} /> {t('sohozJob.backToTools')}
                </button>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                    <div className="flex items-center gap-3">
                        <span className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                            <IconBriefcase size={22} stroke={1.7} />
                        </span>
                        <div>
                            <h1 className="text-xl sm:text-2xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">
                                {t('sohozJob.title')}
                            </h1>
                            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">{t('sohozJob.subtitle')}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2.5 flex-shrink-0">
                        <button
                            onClick={() => navigate('/tools/form-auto-fillup/settings')}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2.5 border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 text-[13px] font-semibold rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                        >
                            <IconSettings size={16} /> {t('sohozJob.settings')}
                        </button>
                        <button
                            onClick={handleCreate}
                            className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-primary hover:bg-primary/90 text-white text-[13px] font-semibold rounded-lg transition-colors"
                        >
                            <IconPlus size={16} stroke={2} /> {t('sohozJob.createCustomer')}
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Search ── */}
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl p-4 shadow-sm">
                <div className="flex items-center gap-2 h-11 px-3.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 focus-within:ring-2 focus-within:ring-primary/40">
                    <IconSearch size={18} className="text-zinc-400 dark:text-zinc-500 flex-shrink-0" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t('sohozJob.searchCustomers')}
                        className="flex-1 bg-transparent outline-none text-sm text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
                    />
                    {search && (
                        <button onClick={() => setSearch('')} className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300">
                            <IconX size={16} />
                        </button>
                    )}
                </div>
            </div>

            {/* ── Content ── */}
            {loading ? (
                <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl shadow-sm overflow-hidden animate-pulse">
                    <div className="px-5 py-3 border-b border-zinc-100 dark:border-zinc-800">
                        <div className="h-3.5 w-24 rounded bg-zinc-200 dark:bg-zinc-800" />
                    </div>
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-3.5 px-5 py-3.5 border-b border-zinc-50 dark:border-zinc-800/60 last:border-0">
                            <div className="w-9 h-9 rounded-lg bg-zinc-200 dark:bg-zinc-800 flex-shrink-0" />
                            <div className="flex-1 min-w-0 space-y-2">
                                <div className="h-3.5 w-40 max-w-[60%] rounded bg-zinc-200 dark:bg-zinc-800" />
                                <div className="h-3 w-24 max-w-[35%] rounded bg-zinc-100 dark:bg-zinc-800/70" />
                            </div>
                            <div className="hidden md:block h-3.5 w-28 rounded bg-zinc-100 dark:bg-zinc-800/70" />
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                <div className="w-8 h-8 rounded-lg bg-zinc-100 dark:bg-zinc-800/70" />
                                <div className="w-8 h-8 rounded-lg bg-zinc-100 dark:bg-zinc-800/70" />
                            </div>
                        </div>
                    ))}
                </div>
            ) : customers.length === 0 ? (
                <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl py-16 text-center shadow-sm">
                    <div className="flex flex-col items-center gap-3">
                        <IconUsersGroup size={44} stroke={1.4} className="text-zinc-300 dark:text-zinc-600" />
                        <p className="text-base font-semibold text-zinc-600 dark:text-zinc-300">
                            {search ? t('sohozJob.noResults') : t('sohozJob.noCustomers')}
                        </p>
                        {!search && (
                            <>
                                <p className="text-sm text-zinc-400 dark:text-zinc-500">{t('sohozJob.noCustomersDesc')}</p>
                                <button
                                    onClick={handleCreate}
                                    className="mt-1 inline-flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary/90 text-white text-[13px] font-semibold rounded-lg transition-colors"
                                >
                                    <IconPlus size={16} /> {t('sohozJob.createCustomer')}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            ) : (
                <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl shadow-sm overflow-hidden">
                    {/* Count bar */}
                    <div className="px-5 py-3 border-b border-zinc-100 dark:border-zinc-800">
                        <span className="text-[13px] font-semibold text-zinc-500 dark:text-zinc-400">
                            {t('sohozJob.customerCount', { count: customers.length })}
                        </span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[12px] uppercase tracking-wide text-zinc-400 dark:text-zinc-500 border-b border-zinc-100 dark:border-zinc-800">
                                    <th className="px-5 py-3 font-semibold">{t('sohozJob.name')}</th>
                                    <th className="px-5 py-3 font-semibold">{t('sohozJob.phone')}</th>
                                    <th className="px-5 py-3 font-semibold hidden md:table-cell">{t('sohozJob.fatherName')}</th>
                                    <th className="px-5 py-3 font-semibold text-right">{t('sohozJob.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {customers.map((c) => (
                                    <tr
                                        key={c.id}
                                        className="border-b border-zinc-50 dark:border-zinc-800/60 last:border-0 hover:bg-zinc-50/60 dark:hover:bg-zinc-800/40 transition-colors"
                                    >
                                        <td className="px-5 py-3">
                                            <div className="flex items-center gap-3">
                                                <span className="w-9 h-9 rounded-lg bg-zinc-100 dark:bg-zinc-800 overflow-hidden flex items-center justify-center flex-shrink-0">
                                                    {c.photo ? (
                                                        <img src={c.photo} alt="" className="w-full h-full object-cover" />
                                                    ) : (
                                                        <IconUser size={18} className="text-zinc-400 dark:text-zinc-500" />
                                                    )}
                                                </span>
                                                <div className="min-w-0">
                                                    <p className="font-semibold text-zinc-900 dark:text-zinc-100 truncate">{c.applicant_name_en || c.applicant_name_bn || '—'}</p>
                                                    {(c.father_name_en || c.father_name_bn) && (
                                                        <p className="text-xs text-zinc-400 dark:text-zinc-500 truncate md:hidden">{c.father_name_en || c.father_name_bn}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-5 py-3 whitespace-nowrap">
                                            {c.mobile_number ? (
                                                <span className="inline-flex items-center gap-1.5">
                                                    <span className="text-zinc-600 dark:text-zinc-300 font-mono text-[13px]">{c.mobile_number}</span>
                                                    <button
                                                        onClick={(e) => copyPhone(e, c)}
                                                        title={t('common.copy')}
                                                        className="w-6 h-6 rounded-md text-zinc-400 hover:text-primary hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center justify-center transition-colors"
                                                    >
                                                        {copiedId === c.id ? <IconCheck size={14} className="text-emerald-500" /> : <IconCopy size={14} />}
                                                    </button>
                                                </span>
                                            ) : <span className="text-zinc-400">—</span>}
                                        </td>
                                        <td className="px-5 py-3 text-zinc-600 dark:text-zinc-300 hidden md:table-cell">{c.father_name_en || c.father_name_bn || '—'}</td>
                                        <td className="px-5 py-3">
                                            <div className="flex items-center justify-end gap-1.5">
                                                {/* Edit — labeled, outside */}
                                                <button
                                                    onClick={() => handleEdit(c)}
                                                    className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 text-sm font-medium hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
                                                >
                                                    <IconEdit size={15} />
                                                    {t('common.edit')}
                                                </button>

                                                {/* More actions — dropdown */}
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <button
                                                            className="w-8 h-8 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 flex items-center justify-center hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors"
                                                        >
                                                            <IconDotsVertical size={16} />
                                                        </button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="w-52">
                                                        <DropdownMenuItem className="gap-2.5" onClick={() => setDocModal({ customerId: c.id, action: 'preview' })}>
                                                            <IconEye size={15} className="shrink-0 text-blue-600 dark:text-blue-400" />
                                                            {t('sohozJob.previewData')}
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem className="gap-2.5" onClick={() => setDocModal({ customerId: c.id, action: 'print' })}>
                                                            <IconPrinter size={15} className="shrink-0 text-violet-600 dark:text-violet-400" />
                                                            {t('sohozJob.printData')}
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem className="gap-2.5" onClick={() => setDocModal({ customerId: c.id, action: 'pdf' })}>
                                                            <IconDownload size={15} className="shrink-0 text-emerald-600 dark:text-emerald-400" />
                                                            {t('sohozJob.downloadPdfData')}
                                                        </DropdownMenuItem>
                                                        {/* Payment SMS the extension saved (Teletalk last step) — its own tab, to keep the list open */}
                                                        <DropdownMenuItem className="gap-2.5" onClick={() => window.open(`/tools/form-auto-fillup/customers/${c.id}/messages`, '_blank', 'noopener')}>
                                                            <IconMessage size={15} className="shrink-0 text-amber-600 dark:text-amber-400" />
                                                            {t('sohozJob.messagesMenu')}
                                                        </DropdownMenuItem>
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuItem
                                                            onClick={() => setDeleteTarget(c)}
                                                            className="gap-2.5 text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400"
                                                        >
                                                            <IconTrash size={15} className="shrink-0" />
                                                            {t('common.delete')}
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ── Delete confirmation ── */}
            <ConfirmDialog
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDeleteConfirm}
                title={t('sohozJob.deleteCustomer')}
                description={`${t('sohozJob.confirmDelete')} "${deleteTarget?.applicant_name_en || deleteTarget?.applicant_name_bn || ''}"?`}
            />

            {/* ── Preview / Print / PDF ── */}
            {docModal && (
                <CustomerDataModal
                    open
                    onClose={() => setDocModal(null)}
                    customerId={docModal.customerId}
                    action={docModal.action}
                />
            )}
        </div>
    );
}
